import { Component } from '@angular/core';
import { AuthService } from '../../../services/auth.service';

interface PolicySection {
  title: string;
  paragraphs: string[];
  bullets?: string[];
}

@Component({
  selector: 'app-privacy-policy',
  templateUrl: './privacy-policy.component.html',
  standalone: false,
})
export class PrivacyPolicyComponent {
  constructor(private authService: AuthService) {}

  protected isAuthenticated(): boolean {
    return Boolean(this.authService.getUserGuid());
  }

  content: PolicySection[] = [
    {
      title: 'Last Updated',
      paragraphs: ['July 10, 2026'],
    },
    {
      title: 'Who Is Responsible',
      paragraphs: [
        'Kekeno Surveys is operated by Kekeno Tech Ltd. This notice explains how we handle personal information when people create accounts, build questionnaires, invite respondents, complete questionnaires, view analytics, receive notifications, or contact us.',
        'For privacy questions, account requests, or data rights requests, contact us at privacy@kekeno.co.nz.',
      ],
    },
    {
      title: 'Data We Collect',
      paragraphs: [
        'We collect and store the information needed to provide account-based questionnaire and survey workflows. Depending on how you use Kekeno Surveys, this may include:',
      ],
      bullets: [
        'Account and user details, such as name, email address, account membership, account role, email verification status, two-factor setup status, theme preference, authentication tokens, and account activity metadata.',
        'Organisation and account details, such as account name, display name, account type, invited users, user access records, and related administration settings.',
        'Questionnaire and question-set content, such as questionnaire names, descriptions, settings, versions, questions, answer options, scoring, branching rules, alert intents, automation tags, imported JSON or XML question sets, release metadata, translations, and uploaded question or answer attachments.',
        'Respondent and questionnaire-completer details, such as first name, last name, email address, date of birth, mobile number, NHI number, student ID, ethnicity, custom details form answers, invitation status, scheduled questionnaire metadata, and the account or user that initiated a questionnaire.',
        'Questionnaire response data, such as selected answers, free-text answers, answer scores, completion status, progress, timestamps, associated questionnaires, response summaries, analytics, trends, and generated reports.',
        'Invite, notification, and communication data, such as invite links, email or SMS dispatch type, recipient details, notification titles, notification bodies, read status, notification links, unread counts, and notification digest email preferences.',
        'OAuth and integration administration data, such as OAuth client registrations, authorised redirect URLs, requested scopes, and authentication codes or tokens needed to support connected clients.',
        'Security, support, and diagnostic data, such as IP address, browser or device details, user agent, operational logs, error logs, support messages, password reset activity, email verification activity, and abuse-prevention records.',
        'Analytics data, such as Firebase Analytics events and technical identifiers when analytics is enabled.',
      ],
    },
    {
      title: 'Sensitive Information',
      paragraphs: [
        'Kekeno Surveys can be configured by account users to collect sensitive information, including health identifiers, health-related answers, ethnicity, student information, or other special-category information. Account owners and administrators are responsible for ensuring they have a lawful basis, consent, authority, and notices required for the questionnaires they create and the respondents they invite.',
        'We ask users to collect only the information needed for their questionnaire purpose and to avoid entering unnecessary sensitive information into questions, free-text answers, attachments, notes, or support messages.',
      ],
    },
    {
      title: 'How We Use Data',
      paragraphs: ['We process personal information for these purposes:'],
      bullets: [
        'To create and manage accounts, authenticate users, support account switching, verify email addresses, provide two-factor authentication, and keep the service secure.',
        'To build, import, version, publish, translate, and manage questionnaires, question sets, settings, attachments, invite links, schedules, and respondent workflows.',
        'To initialise, resume, complete, and analyse questionnaire instances, including response summaries, charts, insights, reports, and associated questionnaire views.',
        'To send service messages, questionnaire invites, verification emails, password reset emails, notification emails, and unread-notification digest emails where enabled.',
        'To provide OAuth client authentication and redirects where a user or client is authorised to use that feature.',
        'To respond to support requests, privacy requests, account administration requests, security issues, and operational incidents.',
        'To understand usage, diagnose issues, improve the product, monitor performance, prevent abuse, and protect the service.',
      ],
    },
    {
      title: 'Sharing and Service Providers',
      paragraphs: [
        'We do not sell personal information. We do not share personal information with third parties for their own advertising. We use service providers and integration partners needed to operate Kekeno Surveys, including:',
      ],
      bullets: [
        'Hosting, database, storage, backup, logging, monitoring, and infrastructure providers.',
        'Private file storage providers for uploaded questionnaire attachments, images, and imported files.',
        'Email and SMS delivery providers for account, invite, verification, notification, and service messages.',
        'Analytics providers, such as Firebase Analytics, where analytics is enabled.',
        'OAuth clients or connected systems that you authorise or administer through the platform.',
      ],
    },
    {
      title: 'Who Can See Data',
      paragraphs: [
        'Account users may see account, questionnaire, respondent, response, analytics, invite, schedule, and notification information according to their account access and role. Respondents see the questionnaire flow and details relevant to the questionnaire instance they are completing.',
        'If an account user creates a questionnaire or invites a respondent, they are responsible for ensuring that access is appropriate for their organisation and that respondent information is handled lawfully.',
      ],
    },
    {
      title: 'Security and Retention',
      paragraphs: [
        'We use practical security controls, authenticated access, role-based account access, secure cloud hosting, private storage, operational monitoring, and backup processes to protect data and support service continuity.',
        'We keep personal information only for as long as needed for the purposes described above, unless a longer period is required for security, legal, accounting, dispute resolution, backup, or service integrity reasons.',
      ],
      bullets: [
        'Account, questionnaire, respondent, response, invite, schedule, notification, and analytics records are generally retained while the relevant account or questionnaire workflow remains active or needed.',
        'OAuth tokens and integration records are kept while the relevant integration remains connected or needed for audit, security, or service integrity.',
        'Support enquiries, security logs, operational logs, and admin records may be kept for a limited period needed to operate and protect the service.',
        'If deletion is requested, we delete, anonymise, or minimise personal information where practical, subject to legal, security, backup, and service integrity requirements.',
        'Backups may retain deleted data for a limited backup lifecycle before being overwritten.',
      ],
    },
    {
      title: 'International Transfers',
      paragraphs: [
        'Kekeno Surveys is operated from New Zealand and uses cloud, communication, analytics, and infrastructure providers that may process data in other countries. Where required, we rely on appropriate safeguards such as provider data processing terms, standard contractual clauses, adequacy decisions, or equivalent transfer mechanisms.',
      ],
    },
    {
      title: 'Your Choices and Rights',
      paragraphs: [
        'You can update some account details in the product, manage notification preferences, and ask us for help with access, correction, deletion, export, restriction, objection, or privacy concerns.',
        'Depending on where you live, you may have rights to request access to your personal information, correction, deletion, restriction, objection, portability, withdrawal of consent, and review by a human where automated decisions have legal or similarly significant effects. Kekeno Surveys does not currently make automated decisions with legal or similarly significant effects about users or respondents.',
      ],
    },
    {
      title: 'Children and Respondents',
      paragraphs: [
        'Kekeno Surveys may be used by organisations to invite respondents, including students or young people, where the organisation has authority to do so. Account owners and administrators are responsible for obtaining any required parent, guardian, school, healthcare, or organisational permissions before collecting respondent data.',
        'If you believe a child or respondent has provided personal information without appropriate authority, contact us so we can review the issue with the relevant account owner.',
      ],
    },
    {
      title: 'Privacy Governance',
      paragraphs: [
        'We use internal privacy and security review practices to keep this notice accurate, review provider categories, apply retention rules, and assess new features such as notifications, OAuth integrations, questionnaire imports, analytics, and file attachments.',
        'Our controls are designed to align with privacy principles including transparency, data minimisation, purpose limitation, security, retention control, and user rights. Operational details are not published in full because they may include security-sensitive information.',
      ],
    },
    {
      title: 'Changes to This Notice',
      paragraphs: [
        'We may update this notice as Kekeno Surveys changes. The latest version will be posted on this page with the updated date above. Continued use of the service means you accept the updated notice.',
      ],
    },
    {
      title: 'Contact',
      paragraphs: [
        'For privacy questions, account requests, or data rights requests, contact us at privacy@kekeno.co.nz.',
      ],
    },
  ];
}
